/**
 * Cookieless page views and the first touch (docs/SPEC.md decisions 42, 44). **Opt-in only** (DPDP consent): nothing
 * is sent or stored until the visitor allows usage statistics; "No" (or withdrawing later) clears what was stored.
 * Browsers asking not to be tracked (Global Privacy Control / Do Not Track) count as "No" and aren't asked.
 * Paths are reduced to templates and referrers to domains, here and again on the server.
 * Browser storage: localStorage `vw.consent` (the choice itself), and only after a yes: sessionStorage `vw.visit`
 * (this tab's source) and localStorage `vw.ft` (where this browser first came from; sent only with a sign-up).
 * Also only after a yes: our web analytics, Minilytics (decision 96), loaded from `/api/public/web-analytics`. It sets the
 * cookies `ml_vid` (visitor, 12 months) and `ml_sid` (visit, 30 minutes), shared with the website in production, learns
 * the signed-in user's id and email, and its ids go into Dodo checkout metadata so a payment is credited to its source.
 */
import { cleanUtm, pathTemplate, referrerDomain } from '@vw/core/src/web-analytics';
import { PRIVACY_NOTICE } from '@vw/core/src/privacy';

const FT_KEY = 'vw.ft';
const VISIT_KEY = 'vw.visit';
const CHOICE_KEY = 'vw.consent';

interface Visit { referrer: string; source: string; medium: string; campaign: string; ref: boolean }

const browserOptOut = () => (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true || navigator.doNotTrack === '1';

/** The visitor's usage-statistics choice: true, false, or null (not asked yet). GPC/DNT = false. */
export function analyticsChoice(): boolean | null {
  if (browserOptOut()) return false;
  try {
    const v = JSON.parse(localStorage.getItem(CHOICE_KEY) ?? 'null') as { analytics?: unknown; version?: unknown } | null;
    return v && typeof v.analytics === 'boolean' && v.version === PRIVACY_NOTICE.version ? v.analytics : null;
  } catch {
    return null;
  }
}

/** Saves the choice; "No" deletes anything stored for statistics. */
export function setAnalyticsChoice(granted: boolean) {
  try {
    localStorage.setItem(CHOICE_KEY, JSON.stringify({ analytics: granted, version: PRIVACY_NOTICE.version, at: Date.now() }));
    if (!granted) { localStorage.removeItem(FT_KEY); sessionStorage.removeItem(VISIT_KEY); }
  } catch { /* storage blocked */ }
  if (granted) { last = ''; trackPageview(); loadWebAnalytics(); }
  else stopWebAnalytics();
}

// ---- Minilytics (decision 96)

type Minilytics = ((cmd: string, a?: unknown, b?: unknown) => void) & { q?: unknown[][]; attribution?: () => Record<string, string> };
declare global { interface Window { minilytics?: Minilytics; minilyticsUrl?: (href: string, kind: string) => string; minilyticsTemplate?: (href: string) => string } }

const SHARE_LINK = /^\/(r|l)\/[^/]+/;
/**
 * What the tracker sends (owner, 2026-10-26: collect everything): the full path and query, including which creator a
 * page shows, plus its template (`/c/:handle`) for grouping in the dashboard. Exceptions: share-link tokens (`/r/…`,
 * `/l/…` are bearer secrets: anyone with one can open the report or collection) become `/r/:token`; the tracker itself
 * drops email, name, token, code and similar parameters; the hash is never sent.
 */
export function webAnalyticsUrl(href: string, kind: string): string {
  const u = new URL(href);
  if (kind === 'page' && SHARE_LINK.test(u.pathname)) u.pathname = pathTemplate(u.pathname);
  // A beta invite code gets someone an account, and a waitlist confirmation token confirms an entry (decision 106):
  // neither is ever sent to analytics.
  u.searchParams.delete('invite');
  u.searchParams.delete('confirm');
  u.hash = '';
  return u.toString();
}

/** The page's template for the dashboard's "Page template" tab and filter, e.g. /c/:handle. */
export function webAnalyticsTemplate(href: string): string {
  return pathTemplate(new URL(href).pathname);
}

const ML_COOKIES = ['ml_vid', 'ml_sid'];
type WebAnalyticsConfig = { host: string | null; siteId: string | null; cookieDomain: string | null };
let mlRequested = false;

/** The tracker's settings (host, site, shared cookie domain), or null when unset or unreachable. */
function webAnalyticsConfig(): Promise<WebAnalyticsConfig | null> {
  return fetch('/api/public/web-analytics').then((r) => (r.ok ? (r.json() as Promise<WebAnalyticsConfig>) : null)).catch(() => null);
}

/** Deletes the tracker's cookies on these domains: host-only ('') and the configured shared domain, never a guessed one. */
export function deleteWebAnalyticsCookies(domains: (string | null)[]) {
  for (const n of ML_COOKIES) {
    for (const d of new Set(['', ...domains.filter((x): x is string => !!x)])) {
      document.cookie = `${n}=; path=/; max-age=0; SameSite=Lax${d ? `; domain=${d}` : ''}${location.protocol === 'https:' ? '; Secure' : ''}`;
    }
  }
}

/**
 * Removes the tracker's cookies, host-only and on the shared domain the API configures (the website may have set them).
 * The domain is never inferred from the hostname: staging (app-staging.<root>) uses host-only cookies and must not
 * delete the production website's shared ones.
 */
export function clearWebAnalyticsCookies() {
  deleteWebAnalyticsCookies([]);
  void webAnalyticsConfig().then((cfg) => { if (cfg?.cookieDomain) deleteWebAnalyticsCookies([cfg.cookieDomain]); });
}

/**
 * On app start: load the tracker with consent; delete its cookies only after a "No" or a browser opt-out. Not asked
 * yet: the website's cookies stay, so a "Yes" on the banner keeps the website visit, sign-up and payment as one journey.
 */
export function startWebAnalytics() {
  const c = analyticsChoice();
  if (c === true) loadWebAnalytics();
  else if (c === false) clearWebAnalyticsCookies();
}

/** Loads the tracker once, only with consent. Calls made before it loads are queued and replayed by the script. */
export function loadWebAnalytics() {
  if (optedOut() || mlRequested) return;
  mlRequested = true;
  window.minilyticsUrl = webAnalyticsUrl;
  window.minilyticsTemplate = webAnalyticsTemplate;
  if (!window.minilytics) {
    const q: unknown[][] = [];
    const stub = ((...args: unknown[]) => { q.push(args); }) as Minilytics;
    stub.q = q;
    window.minilytics = stub;
  }
  void webAnalyticsConfig().then((cfg) => {
    if (!cfg?.host || !cfg.siteId || optedOut() || document.getElementById('vw-ml')) return;
    const s = document.createElement('script');
    s.id = 'vw-ml';
    s.defer = true;
    s.src = `${cfg.host}/js/script.js`;
    s.dataset.websiteId = cfg.siteId;
    if (cfg.cookieDomain) s.dataset.domain = cfg.cookieDomain;
    document.head.appendChild(s);
  });
}

/** Withdrawing: delete the cookies; a loaded tracker is removed by reloading the page. */
function stopWebAnalytics() {
  clearWebAnalyticsCookies();
  if (document.getElementById('vw-ml')) location.reload();
}

/** Tells the tracker who is signed in (user id + email; owner decision 2026-10-26). Only with consent. */
export function identifyWebAnalytics(user: { id: string; email: string } | null) {
  if (!user || optedOut()) return;
  loadWebAnalytics();
  window.minilytics?.('identify', { user_id: user.id, email: user.email });
}

/** A goal (e.g. checkout_start), only with consent. */
export function webAnalyticsGoal(name: string, props?: Record<string, string>) {
  if (optedOut()) return;
  window.minilytics?.('goal', name, props);
}

/** Visitor and visit ids for checkout metadata, or undefined without consent or before the tracker has loaded. */
export function webAnalyticsAttribution(): Record<string, string> | undefined {
  if (optedOut()) return undefined;
  const a = window.minilytics?.attribution?.();
  return a && a.ml_visitor_id ? a : undefined;
}

const optedOut = () => analyticsChoice() !== true;

/** This tab's source, fixed at its first page (landing). */
function visitFor(landing: boolean): Visit {
  if (!landing) {
    try {
      const v = JSON.parse(sessionStorage.getItem(VISIT_KEY) ?? 'null') as Visit | null;
      if (v) return v;
    } catch { /* fall through */ }
  }
  const q = new URLSearchParams(location.search);
  const v: Visit = {
    referrer: referrerDomain(document.referrer, [location.hostname]),
    source: cleanUtm(q.get('utm_source')), medium: cleanUtm(q.get('utm_medium')), campaign: cleanUtm(q.get('utm_campaign')),
    ref: !!q.get('ref'),
  };
  if (!optedOut()) {
    try { sessionStorage.setItem(VISIT_KEY, JSON.stringify(v)); } catch { /* storage blocked */ }
  }
  return v;
}

/** Remembers the first touch once per browser (templated; not when opted out). */
function rememberFirstTouch(v: Visit) {
  if (optedOut()) return;
  try {
    if (localStorage.getItem(FT_KEY)) return;
    localStorage.setItem(FT_KEY, JSON.stringify({ ...v, landing: pathTemplate(location.pathname), at: Date.now() }));
  } catch { /* storage blocked */ }
}

/** The first touch to send with a sign-up (`ft` query parameter), or ''. */
export function firstTouchParam(): string {
  if (optedOut()) return '';
  try {
    const v = localStorage.getItem(FT_KEY);
    return v && v.length < 600 ? `&ft=${encodeURIComponent(v)}` : '';
  } catch {
    return '';
  }
}

let last = '';
/** One page view per path change, only with consent. */
export function trackPageview() {
  if (optedOut()) return;
  const path = location.pathname;
  if (path === last) return;
  last = path;
  // Landing = the first page of this tab's visit (a reload keeps the visit and its source).
  let landing = true;
  try { landing = !sessionStorage.getItem(VISIT_KEY); } catch { landing = true; }
  const visit = visitFor(landing);
  if (landing) rememberFirstTouch(visit);
  const body = JSON.stringify({ path: pathTemplate(path), ...visit, landing, consent: true });
  try {
    void fetch('/api/t', { method: 'POST', body, headers: { 'content-type': 'application/json' }, keepalive: true, credentials: 'same-origin' }).catch(() => undefined);
  } catch { /* ignore */ }
}
